import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy,
  inject,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, catchError, first, of, switchMap, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ThemeService } from '../services/theme.service';
import { HttpClient } from '@angular/common/http';

import { Ticket } from '@shared/entities/ticket';
import { MachineAlarmFilterURL, MachineAlarmsService } from '@shared/services/api/machine-alarms.service';
import { MachineFiltersService } from '@shared/services/api/machine-filters.service';
import { ProfileService } from '@shared/services/api/profile.service';
import { TicketService } from '@shared/services/api/ticket.service';
import { PhoneNumberRegExp } from '@shared/utils';
import { MAXLENGTH_TEXTAREA } from '@shared/models/constants';
import { Location } from '@angular/common';

interface NamedOption {
  name: string;
  id: string;
}

interface ErrorCode {
  dec: string;
  label: string;
}

@Component({
  selector: 'app-new-ticket',
  templateUrl: './new-ticket.component.html',
  styleUrl: './new-ticket.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NewTicketComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _formBuilder = inject(FormBuilder);
  public translateService = inject(TranslateService);
  private _themeService = inject(ThemeService);
  private _ticketService = inject(TicketService);
  private _activatedRoute = inject(ActivatedRoute);
  private _profileService = inject(ProfileService);
  private _machineFilterService = inject(MachineFiltersService);
  private _machineAlarmService = inject(MachineAlarmsService);
  private _location = inject(Location);
  private _http = inject(HttpClient);

  errorCodeList$!: Observable<[string]>;
  form!: FormGroup;
  isLight = true;
  isLoading = false;
  serialNumberOptions: NamedOption[] = [];
  fetchSerialNumbers = (searchTerm: string) => this.fetchOptions(this.serialNumberOptions, searchTerm);
  newTicket?: Ticket;
  showError = false;
  showSuccess = false;
  @ViewChild('fileInput') fileInput: ElementRef = {} as ElementRef;

  readonly MAXLENGTH_TEXTAREA = MAXLENGTH_TEXTAREA;

  /** Lista statica completa dei codici errore dall'Excel (SILO + RIGO) */
  staticErrorCodes: ErrorCode[] = [];
  /** Lista filtrata in base al testo digitato nel campo predictive */
  filteredErrorCodes: ErrorCode[] = [];
  /** Controlla la visibilità del dropdown autocomplete */
  showDropdown = false;

  constructor() {
    this.form = this._formBuilder.group({
      firstName: ['', Validators.required],
      lastName: ['', Validators.required],
      email: [{ value: '', disabled: true }, Validators.required],
      phone: ['', [Validators.pattern(PhoneNumberRegExp)]],
      serialNumber: ['', Validators.required],
      description: [''],
      errorMessage: [''], // opzionale
      attachmentBody: [''],
      attachmentFileName: ['']
    });
  }

  ngOnInit() {
    // Carica la lista statica dei codici errore dall'Excel
    this._http.get<ErrorCode[]>('assets/error-codes.json').pipe(
      first(),
      tap((codes) => {
        this.staticErrorCodes = codes;
        this.filteredErrorCodes = [...codes.slice(0, 10)];
        this._cdr.markForCheck();
      }),
      catchError(() => {
        this.staticErrorCodes = [];
        this.filteredErrorCodes = [];
        return of([]);
      })
    ).subscribe();

    this._activatedRoute.queryParams
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        first(),
        switchMap((params) => {
          if (params['serialNumber']) {
            this.form.patchValue({
              serialNumber: params['serialNumber']
            });
            this.form.get('serialNumber')?.disable();
            this._machineAlarmService.filters = { serialNumber: params['serialNumber'] };
            this._cdr.markForCheck();
            return of([params['serialNumber']]);
          }
          return this._machineFilterService.getSerialNumbersForModule('TICKET').pipe(
            tap((serialNumbers) => {
              if (serialNumbers?.length === 1) {
                this.form.patchValue({ serialNumber: serialNumbers[0] });
                this._machineAlarmService.filters = { serialNumber: serialNumbers[0] } as MachineAlarmFilterURL;
              }
              this._cdr.markForCheck();
            })
          );
        }),
        tap((serialNumbers: string[]) => {
          this.serialNumberOptions = this.toNamedOptions(serialNumbers || []);
          this._cdr.markForCheck();
        })
      )
      .subscribe();

    this.errorCodeList$ = this._activatedRoute.queryParams.pipe(
      takeUntilDestroyed(this._destroyRef),
      first(),
      switchMap((params) => {
        if (params['errorMessage'] && params['errorMessage'] !== 'undefined') {
          this.form.patchValue({
            errorMessage: params['errorMessage']
          });
          this.form.get('errorMessage')?.disable();
          this._cdr.markForCheck();
          return of([params['errorMessage']] as [string]);
        }
        return this._machineAlarmService.all$.pipe(tap(() => this._cdr.markForCheck()));
      })
    );

    this._profileService.profile$
      .pipe(
        first(),
        takeUntilDestroyed(this._destroyRef),
        tap((profile) => {
          this.form.patchValue({
            firstName: profile.firstName,
            lastName: profile.lastName,
            email: profile.email,
            phone: profile.mobilePhone || profile.phone
          });
          if (profile.firstName) this.form.get('firstName')?.disable();
          if (profile.lastName) this.form.get('lastName')?.disable();
          this._cdr.markForCheck();
        })
      )
      .subscribe();

    this._themeService.isLight$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((x) => {
          this.isLight = x;
          this._cdr.markForCheck();
        })
      )
      .subscribe();

    this.form
      .get('serialNumber')!
      .valueChanges.pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((serialNumber: string) => {
          if (serialNumber) {
            this.onSelectedSerialNumber(serialNumber);
          }
        })
      )
      .subscribe();
  }

  private toNamedOptions(items: string[]): NamedOption[] {
    return items.map((item) => ({ name: item, id: item }));
  }

  private fetchOptions(options: NamedOption[], searchTerm: string): Observable<NamedOption[]> {
    if (!searchTerm) return of(options);
    const term = searchTerm.toLowerCase();
    return of(options.filter((o) => o.name.toLowerCase().includes(term)));
  }

  onSelectedSerialNumber(selectedSerialNumber: string) {
    this._machineAlarmService.filters = {
      serialNumber: selectedSerialNumber
    } as MachineAlarmFilterURL;
    this.errorCodeList$ = this._machineAlarmService.all$;
    this.form.get('errorMessage')?.enable();
  }

  /**
   * Filtra la lista statica dei codici errore in base al testo digitato.
   * Cerca sia nel codice hex che nella descrizione.
   */
  onErrorCodeInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value.toLowerCase().trim();
    if (!value) {
      this.filteredErrorCodes = this.staticErrorCodes.slice(0, 10);
    } else {
      this.filteredErrorCodes = this.staticErrorCodes
        .filter((code) => code.dec.toLowerCase().includes(value))
        .slice(0, 10);
    }
    this.showDropdown = true;
    this._cdr.markForCheck();
  }

  onErrorCodeFocus(): void {
    if (this.filteredErrorCodes.length > 0) {
      this.showDropdown = true;
      this._cdr.markForCheck();
    }
  }

  onErrorCodeBlur(): void {
    // Ritardo per permettere il click sull'opzione prima che il blur chiuda il dropdown
    setTimeout(() => {
      this.showDropdown = false;
      this._cdr.markForCheck();
    }, 150);
  }

  selectErrorCode(dec: string): void {
    this.form.patchValue({ errorMessage: dec });
    this.showDropdown = false;
    this._cdr.markForCheck();
  }

  /**
   * This input is the only text field in the form, so pressing Enter while typing a code
   * submits the whole ticket by default browser behaviour instead of just confirming the
   * selection. Stop that, and confirm the top suggestion instead, mirroring what Enter does
   * in a standard autocomplete.
   */
  onErrorCodeEnter(event: Event): void {
    event.preventDefault();
    const typed = (event.target as HTMLInputElement).value.trim();
    // Only auto-confirm when the user actually narrowed the list down: an Enter on the
    // untouched, unfiltered top-10 default list should not silently pick an arbitrary code.
    if (typed && this.showDropdown && this.filteredErrorCodes.length > 0) {
      this.selectErrorCode(this.filteredErrorCodes[0].dec);
    }
  }

  submitForm() {
    this.isLoading = true;
    this._cdr.markForCheck();
    const filteredValues = Object.fromEntries(Object.entries(this.form.getRawValue()).filter(([, value]) => !!value));
    this._ticketService
      .save(filteredValues)
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((createdTicket) => {
          this.isLoading = false;
          this.showSuccess = true;
          this.showError = false;
          this.newTicket = createdTicket;
          this._ticketService.refreshOpened(1);
          this._cdr.markForCheck();
        }),
        catchError(() => {
          this.showError = true;
          this.isLoading = false;
          this._cdr.markForCheck();
          return of(null);
        })
      )
      .subscribe();
  }

  onFileSelect(event: Event): void {
    const element = event.currentTarget as HTMLInputElement;
    const file: File | null = element.files ? element.files[0] : null;

    if (file) {
      this.form.patchValue({
        attachmentFileName: file.name
      });
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        if (reader.result) {
          const [, elem] = (reader.result as string).split('base64,');
          this.form.patchValue({
            attachmentBody: elem
          });
        }
      };
    }
  }

  getYear() {
    return new Date().getFullYear();
  }

  onResetUpdateFile() {
    this.form.patchValue({
      attachmentFileName: null,
      attachmentBody: null
    });
  }

  goBack() {
    this._location.back();
  }
}
